import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useLocation, useSearch } from 'wouter';
import logoImage from "@assets/logo-e-zez-png-3.png";
import Lightbox from '@/components/Lightbox';

// ─── Types ───────────────────────────────────────────────────────────────────

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  title?: string;
  description?: string;
  location?: string;
  category: string;
  tags: string[];
  uploadedBy?: string;
  projectId?: string;
  isPrimary?: boolean;
}

interface Video {
  id: number;
  videoUrl: string;
  platform: string;
  embedUrl: string;
  title?: string;
  description?: string;
  category: string;
  tags: string[];
  createdAt: string;
}

// ─── Platform icon ────────────────────────────────────────────────────────────

const PlatformIcon = ({ platform, size = 4 }: { platform: string; size?: number }) => {
  const cls = `w-${size} h-${size}`;
  if (platform === 'youtube') return (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );
  if (platform === 'vimeo') return (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197c1.185-1.044 2.351-2.084 3.501-3.128C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.48 4.807z"/>
    </svg>
  );
  return (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
};

// ─── Video Player Modal ───────────────────────────────────────────────────────

const VideoPlayerModal = ({ video, onClose }: { video: Video; onClose: () => void }) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center"
      onClick={onClose}
    >
      {/* Close bar — always visible at top */}
      <div className="w-full flex justify-end px-4 py-3 flex-shrink-0">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-medium bg-white/10 rounded-full px-4 py-2 backdrop-blur-sm border border-white/20"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Close
        </button>
      </div>

      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="relative w-full max-w-5xl px-3 sm:px-6 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aspect-video rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl shadow-black ring-1 ring-white/10 bg-black">
          <iframe
            src={video.embedUrl}
            title={video.title || 'Video'}
            className="w-full h-full"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
            style={{ border: 'none' }}
          />
        </div>

        {(video.title || video.description) && (
          <div className="mt-4 px-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {video.title && <h3 className="text-white text-base sm:text-xl font-bold truncate">{video.title}</h3>}
                {video.description && <p className="text-gray-400 mt-1 text-sm line-clamp-2">{video.description}</p>}
              </div>
              <div className="flex items-center gap-1.5 text-gray-400 text-xs bg-white/5 px-2.5 py-1.5 rounded-full border border-white/10 flex-shrink-0">
                <PlatformIcon platform={video.platform} size={3} />
                <span className="capitalize">{video.platform}</span>
              </div>
            </div>
            {video.tags && video.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {video.tags.map((tag, i) => (
                  <span key={i} className="text-xs bg-white/10 text-gray-300 px-2 py-0.5 rounded-full">{tag}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// ─── Horizontal scroll category pills ────────────────────────────────────────

const CategoryPills = ({
  categories,
  active,
  onChange,
  formatLabel,
}: {
  categories: string[];
  active: string;
  onChange: (c: string) => void;
  formatLabel: (c: string) => string;
}) => (
  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
    {categories.map(cat => (
      <button
        key={cat}
        onClick={() => onChange(cat)}
        className={`flex-shrink-0 text-xs px-3 py-2 rounded-full font-medium transition-all duration-200 ${
          active === cat
            ? 'bg-red-600 text-white shadow-md shadow-red-900/30'
            : 'bg-white/5 text-gray-400 hover:bg-white/10 active:bg-white/15 hover:text-white border border-white/10'
        }`}
      >
        {formatLabel(cat)}
      </button>
    ))}
  </div>
);

// ─── Main Gallery Page ────────────────────────────────────────────────────────

export default function Gallery() {
  const [, navigate] = useLocation();
  const searchString = useSearch();

  const [activeTab, setActiveTab] = useState<'photos' | 'videos'>(() => {
    const params = new URLSearchParams(searchString);
    return params.get('tab') === 'videos' ? 'videos' : 'photos';
  });

  // ── Photos state ────────────────────────────────────────────────────────────
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [filteredImages, setFilteredImages] = useState<GalleryImage[]>([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [photoSearch, setPhotoSearch] = useState('');
  const [photosLoading, setPhotosLoading] = useState(true);
  const [photosError, setPhotosError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // ── Videos state ────────────────────────────────────────────────────────────
  const [videos, setVideos] = useState<Video[]>([]);
  const [filteredVideos, setFilteredVideos] = useState<Video[]>([]);
  const [activeVideoCategory, setActiveVideoCategory] = useState('all');
  const [videoSearch, setVideoSearch] = useState('');
  const [videosLoading, setVideosLoading] = useState(true);
  const [activeVideoModal, setActiveVideoModal] = useState<Video | null>(null);

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const shuffleArray = <T,>(arr: T[]): T[] => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const fetchPhotos = async () => {
    try {
      setPhotosLoading(true);
      setPhotosError(null);
      const res = await fetch('/api/gallery');
      const data = await res.json();
      if (data.success) setGalleryImages(shuffleArray(data.data || []));
      else throw new Error(data.message);
    } catch {
      setPhotosError('Failed to load photos. Please try again.');
      setGalleryImages([]);
    } finally {
      setPhotosLoading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      setVideosLoading(true);
      const res = await fetch('/api/videos');
      const data = await res.json();
      if (data.success) setVideos(data.data || []);
    } catch {}
    finally { setVideosLoading(false); }
  };

  useEffect(() => { fetchPhotos(); fetchVideos(); }, []);

  // Auto-open lightbox from URL
  useEffect(() => {
    if (galleryImages.length > 0 && searchString) {
      const params = new URLSearchParams(searchString);
      const imageId = params.get('image');
      if (imageId) {
        const img = galleryImages.find(i => i.id === parseInt(imageId));
        if (img) { setSelectedImage(img); setLightboxOpen(true); }
        navigate('/gallery', { replace: true });
      }
    }
  }, [galleryImages, searchString]);

  // ── Filter ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const filtered = galleryImages.filter(img => {
      const catOk = activeCategory === 'all' || img.category === activeCategory;
      const searchOk = !photoSearch ||
        img.alt.toLowerCase().includes(photoSearch.toLowerCase()) ||
        (img.title && img.title.toLowerCase().includes(photoSearch.toLowerCase())) ||
        (img.description && img.description.toLowerCase().includes(photoSearch.toLowerCase()));
      return catOk && searchOk;
    });
    setFilteredImages(filtered);
  }, [galleryImages, activeCategory, photoSearch]);

  useEffect(() => {
    const filtered = videos.filter(v => {
      const catOk = activeVideoCategory === 'all' || v.category === activeVideoCategory;
      const searchOk = !videoSearch ||
        (v.title && v.title.toLowerCase().includes(videoSearch.toLowerCase())) ||
        (v.description && v.description.toLowerCase().includes(videoSearch.toLowerCase())) ||
        v.category.toLowerCase().includes(videoSearch.toLowerCase());
      return catOk && searchOk;
    });
    setFilteredVideos(filtered);
  }, [videos, activeVideoCategory, videoSearch]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const photoCategories = ['all', ...Array.from(new Set(galleryImages.map(i => i.category)))];
  const videoCategories = ['all', ...Array.from(new Set(videos.map(v => v.category)))];

  const formatCategory = (c: string) =>
    c === 'all'
      ? (activeTab === 'photos' ? 'All Photos' : 'All Videos')
      : c.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const groupedPhotos = filteredImages.reduce((acc, img) => {
    const key = img.projectId || `single-${img.id}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(img);
    return acc;
  }, {} as Record<string, GalleryImage[]>);

  const PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjMWExYTFhIi8+CjxwYXRoIGQ9Ik0yMDAgMTUwTDE1MCAyMDBIMjUwTDIwMCAxNTBaIiBmaWxsPSIjMzMzIi8+CjxjaXJjbGUgY3g9IjE3MCIgY3k9IjEyMCIgcj0iMjAiIGZpbGw9IiMzMzMiLz4KPHRleHQgeD0iMjAwIiB5PSIyNjAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';

  return (
    <div className="min-h-screen flex flex-col bg-gray-950">

      {/* ── Header ───────────────────────────────────────────────────────────── */}
      <div className="bg-white text-black py-3 sticky top-0 z-20 shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <img src={logoImage} alt="Swiss Welding" className="h-10 sm:h-12 w-auto" />

            {/* Mobile: back button */}
            <button
              onClick={() => navigate('/')}
              className="flex md:hidden items-center gap-1.5 text-sm font-semibold text-black bg-gray-100 active:bg-gray-200 rounded-full px-4 py-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Home
            </button>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center space-x-6">
              {['HOME','ABOUT','SERVICES','TESTIMONIALS','TIMELINE','ESTIMATE','BOOK NOW','CONTACT'].map(label => {
                const href = label === 'HOME' ? '/' : `/#${label.toLowerCase().replace(' ', '')}`;
                return (
                  <button key={label} onClick={() => navigate(href)}
                    className="text-black hover:text-red-500 hover:underline transition-colors font-medium text-sm">
                    {label}
                  </button>
                );
              })}
              <button className="text-red-500 font-medium border-b-2 border-red-500 text-sm">GALLERY</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Hero Banner ──────────────────────────────────────────────────────── */}
      <div className="relative h-52 sm:h-72 bg-gradient-to-r from-black via-gray-900 to-red-950 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'repeating-linear-gradient(45deg,#ef4444 0,#ef4444 1px,transparent 0,transparent 50%)',
          backgroundSize: '24px 24px'
        }} />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent" />
        <div className="container mx-auto px-4 h-full flex flex-col justify-center items-center relative z-10 text-white text-center">
          <motion.h1
            className="text-3xl sm:text-4xl md:text-6xl font-black text-white mb-2 sm:mb-3"
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Project Portfolio
          </motion.h1>
          <motion.div className="w-16 sm:w-24 h-1 bg-red-600 mb-3 sm:mb-5"
            initial={{ width: 0 }} animate={{ width: '5rem' }}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
          <motion.p className="text-gray-300 text-sm sm:text-lg max-w-sm sm:max-w-2xl px-2"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Precision welding &amp; stunning craftsmanship — photos &amp; videos
          </motion.p>
        </div>
      </div>

      {/* ── Tab Selector ─────────────────────────────────────────────────────── */}
      <div className="sticky top-[64px] sm:top-[76px] z-10 bg-gray-950 border-b border-white/10 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex">
            {(['photos', 'videos'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative flex-1 sm:flex-none flex items-center justify-center sm:justify-start gap-2 px-4 sm:px-8 py-4 font-semibold text-sm transition-all duration-300 ${
                  activeTab === tab ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {tab === 'photos' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <span className="capitalize">{tab}</span>
                {tab === 'photos' && galleryImages.length > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === 'photos' ? 'bg-red-600 text-white' : 'bg-white/10 text-gray-400'}`}>
                    {galleryImages.length}
                  </span>
                )}
                {tab === 'videos' && videos.length > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === 'videos' ? 'bg-red-600 text-white' : 'bg-white/10 text-gray-400'}`}>
                    {videos.length}
                  </span>
                )}
                {activeTab === tab && (
                  <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-600" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ──────────────────────────────────────────────────────────── */}
      <div className="flex-grow py-5 sm:py-10 bg-gray-950">
        <div className="container mx-auto px-3 sm:px-4">

          {/* Search + Filter */}
          <motion.div
            className="bg-gray-900 border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-10 shadow-xl"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Search row */}
            <div className="flex gap-2 mb-3 sm:mb-4">
              <div className="relative flex-1">
                <Input
                  type="search"
                  placeholder={activeTab === 'photos' ? 'Search photos...' : 'Search videos...'}
                  className="bg-gray-800 border-white/20 text-white placeholder-gray-500 focus:border-red-500 pr-9 h-10"
                  value={activeTab === 'photos' ? photoSearch : videoSearch}
                  onChange={(e) => activeTab === 'photos' ? setPhotoSearch(e.target.value) : setVideoSearch(e.target.value)}
                />
                <svg className="h-4 w-4 absolute right-3 top-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <Button
                onClick={activeTab === 'photos' ? fetchPhotos : fetchVideos}
                variant="outline"
                className="border-white/20 text-gray-400 hover:bg-white/10 hover:text-white h-10 px-3 text-xs flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </Button>
            </div>

            {/* Category pills — horizontal scroll on mobile */}
            <CategoryPills
              categories={activeTab === 'photos' ? photoCategories : videoCategories}
              active={activeTab === 'photos' ? activeCategory : activeVideoCategory}
              onChange={activeTab === 'photos' ? setActiveCategory : setActiveVideoCategory}
              formatLabel={formatCategory}
            />
          </motion.div>

          {/* ── PHOTOS TAB ─────────────────────────────────────────────────── */}
          <AnimatePresence mode="wait">
            {activeTab === 'photos' && (
              <motion.div key="photos"
                initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}
              >
                {photosLoading ? (
                  <div className="text-center py-20">
                    <div className="animate-spin h-10 w-10 border-4 border-red-600 border-t-transparent rounded-full inline-block mb-4" />
                    <p className="text-gray-400 text-sm">Loading photos...</p>
                  </div>
                ) : photosError ? (
                  <div className="text-center py-20">
                    <p className="text-red-400 mb-4 text-sm">{photosError}</p>
                    <Button onClick={fetchPhotos} className="bg-red-600 hover:bg-red-700 text-white">Try Again</Button>
                  </div>
                ) : Object.keys(groupedPhotos).length === 0 ? (
                  <div className="text-center py-20 text-gray-500">
                    <svg className="h-14 w-14 mx-auto mb-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-base">{galleryImages.length === 0 ? 'No photos uploaded yet.' : 'No photos match your search.'}</p>
                    {galleryImages.length > 0 && (
                      <button onClick={() => { setActiveCategory('all'); setPhotoSearch(''); }}
                        className="mt-4 text-red-500 text-sm underline">Clear filters</button>
                    )}
                  </div>
                ) : (
                  <motion.div
                    className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-5"
                    variants={{ show: { transition: { staggerChildren: 0.05 } } }}
                    initial="hidden" animate="show"
                  >
                    {Object.entries(groupedPhotos).map(([key, images]) => {
                      const primary = images.find(i => i.isPrimary) || images[0];
                      const count = images.length;
                      const isProject = !!primary.projectId && count > 1;

                      return (
                        <motion.div
                          key={key}
                          variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                          className="group relative aspect-[4/3] rounded-xl sm:rounded-2xl overflow-hidden bg-gray-900 shadow-lg cursor-pointer ring-1 ring-white/5 active:scale-[0.98] transition-transform duration-150"
                          onClick={() => { setSelectedImage(primary); setLightboxOpen(true); }}
                          whileHover={{ y: -3 }}
                        >
                          <img
                            src={`/api/gallery/${primary.id}/image`}
                            alt={primary.alt}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                          />

                          {/* Always-visible gradient overlay at bottom — shows info on mobile */}
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pt-8 pb-2.5 px-2.5 sm:pb-4 sm:px-4">
                            <h3 className="text-white font-semibold text-xs sm:text-sm leading-tight line-clamp-1">
                              {primary.title || primary.alt}
                            </h3>
                            <p className="text-gray-400 text-[10px] sm:text-xs mt-0.5 capitalize">
                              {primary.category.replace(/-/g, ' ')}
                            </p>
                          </div>

                          {/* Project count badge */}
                          {isProject && (
                            <div className="absolute top-2 right-2 bg-red-600/90 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium">
                              {count} photos
                            </div>
                          )}

                          {/* Desktop hover: extra details + view icon */}
                          <div className="hidden sm:flex absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all duration-300 items-center justify-center">
                            <div className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* ── VIDEOS TAB ───────────────────────────────────────────────── */}
            {activeTab === 'videos' && (
              <motion.div key="videos"
                initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 16 }} transition={{ duration: 0.25 }}
              >
                {videosLoading ? (
                  <div className="text-center py-20">
                    <div className="animate-spin h-10 w-10 border-4 border-red-600 border-t-transparent rounded-full inline-block mb-4" />
                    <p className="text-gray-400 text-sm">Loading videos...</p>
                  </div>
                ) : filteredVideos.length === 0 ? (
                  <div className="text-center py-20 text-gray-500">
                    <svg className="h-14 w-14 mx-auto mb-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-base">{videos.length === 0 ? 'No videos added yet.' : 'No videos match your search.'}</p>
                    {videos.length > 0 && (
                      <button onClick={() => { setActiveVideoCategory('all'); setVideoSearch(''); }}
                        className="mt-4 text-red-500 text-sm underline">Clear filters</button>
                    )}
                  </div>
                ) : (
                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5"
                    variants={{ show: { transition: { staggerChildren: 0.05 } } }}
                    initial="hidden" animate="show"
                  >
                    {filteredVideos.map((video) => (
                      <motion.div
                        key={video.id}
                        variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                        className="group relative aspect-video rounded-xl sm:rounded-2xl overflow-hidden bg-gray-900 shadow-xl cursor-pointer ring-1 ring-white/5 active:scale-[0.98] transition-transform duration-150"
                        onClick={() => setActiveVideoModal(video)}
                        whileHover={{ y: -3 }}
                      >
                        {/* Iframe preview */}
                        <div className="absolute inset-0 pointer-events-none">
                          <iframe
                            src={video.embedUrl}
                            title={video.title || 'Video preview'}
                            className="w-full h-full scale-105"
                            style={{ border: 'none', pointerEvents: 'none' }}
                            tabIndex={-1}
                          />
                        </div>

                        {/* Always-visible overlay — mobile-first */}
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors duration-300 flex flex-col items-center justify-center gap-2">
                          {/* Play button — big enough on mobile */}
                          <div className="w-14 h-14 sm:w-14 sm:h-14 rounded-full bg-red-600 shadow-lg shadow-red-900/60 flex items-center justify-center active:scale-95 transition-transform">
                            <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                          {video.title && (
                            <p className="text-white font-semibold text-xs sm:text-sm text-center px-4 leading-tight line-clamp-2">{video.title}</p>
                          )}
                        </div>

                        {/* Bottom info bar */}
                        <div className="absolute bottom-0 inset-x-0 flex items-center justify-between px-3 py-2 bg-gradient-to-t from-black/80 to-transparent">
                          <span className="text-white/70 text-[10px] capitalize">{video.category.replace(/-/g, ' ')}</span>
                          <div className="flex items-center gap-1 text-white/60">
                            <PlatformIcon platform={video.platform} size={3} />
                            <span className="text-[10px] capitalize">{video.platform}</span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Back to Home */}
          <div className="flex justify-center mt-12 sm:mt-16 mb-4">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-red-600 text-red-500 hover:bg-red-600 hover:text-white transition-all duration-300 text-sm sm:text-base font-medium rounded-full group flex items-center gap-2 bg-transparent"
            >
              Return to Homepage
              <svg className="h-4 w-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-black text-gray-500 py-5 text-center text-xs sm:text-sm border-t border-white/10">
        © {new Date().getFullYear()} Swiss Welding LLC. All rights reserved.
      </div>

      {/* Floating Home — bottom right, 44px+ tap target */}
      <motion.div className="fixed bottom-6 right-4 sm:bottom-8 sm:right-8 z-10"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 1 }}
        whileHover={{ scale: 1.1 }}
      >
        <Button
          onClick={() => navigate('/')}
          className="bg-red-600 text-white hover:bg-red-700 shadow-xl rounded-full h-12 w-12 sm:h-14 sm:w-14 flex items-center justify-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        </Button>
      </motion.div>

      {/* Photo Lightbox */}
      <Lightbox
        isOpen={lightboxOpen}
        onClose={() => { setLightboxOpen(false); setTimeout(() => setSelectedImage(null), 300); }}
        image={selectedImage}
      />

      {/* Video Modal */}
      <AnimatePresence>
        {activeVideoModal && (
          <VideoPlayerModal video={activeVideoModal} onClose={() => setActiveVideoModal(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}
